import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WorkItemService } from "@/gen/omnios/v1/work_item_connect";
import type { ConfigOption } from "@/gen/omnios/v1/work_item_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { Struct } from "@bufbuild/protobuf";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight, Edit2, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/work-items")({
	component: WorkItemSettingsPage,
});

const client = createClient(WorkItemService, transport);

function WorkItemSettingsPage() {
	const { t } = useTranslation("settings");

	const TABS = [
		{ id: "types", label: t("work_items_tab_types") },
		{ id: "work_item_priority", label: t("work_items_tab_priorities") },
		{ id: "order_status", label: t("work_items_tab_order_status") },
	];

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("work_items_breadcrumb") },
	]);

	const [activeTab, setActiveTab] = useState<string>("types");

	return (
		<div className="space-y-6">
			<Tabs value={activeTab} onValueChange={setActiveTab}>
				<TabsList>
					{TABS.map((tab) => (
						<TabsTrigger key={tab.id} value={tab.id}>
							{tab.label}
						</TabsTrigger>
					))}
				</TabsList>

				<TabsContent value="types">
					<TypeEditor />
				</TabsContent>

				<TabsContent value="work_item_priority">
					<ConfigDomainEditor domain="work_item_priority" />
				</TabsContent>

				<TabsContent value="order_status">
					<ConfigDomainEditor domain="order_status" />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// ─── Type editor (types + nested statuses) ────────────────────────

function TypeEditor() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["config-options", "work_item_type"],
		queryFn: () => client.listConfigOptions({ domain: "work_item_type" }),
	});

	const types = data?.options ?? [];

	if (isLoading) {
		return (
			<div className="flex justify-center py-8">
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="space-y-3 pt-2">
			<div className="flex justify-end">
				<Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
					<Plus className="h-3.5 w-3.5 mr-1" />
					{t("work_items_add_type")}
				</Button>
			</div>

			{types.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("work_items_no_types")}
				</p>
			) : (
				<div className="space-y-6">
					{(["case", "task", "sub_task", "other"] as const).map((kindKey) => {
						const group = types.filter((type_opt) => {
							const attrs = (type_opt.attrs?.toJson() ?? {}) as Record<
								string,
								unknown
							>;
							const kind = attrs.kind as string | undefined;
							return kindKey === "other"
								? !kind || !["case", "task", "sub_task"].includes(kind)
								: kind === kindKey;
						});
						if (group.length === 0) return null;
						return (
							<div key={kindKey} className="space-y-1.5">
								<h3 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider px-1">
									{t(`work_items_kind_${kindKey}s` as any) ?? kindKey}
								</h3>
								{group.map((type_opt) => (
									<TypeRow
										key={type_opt.id}
										type_opt={type_opt}
										onChanged={() =>
											qc.invalidateQueries({
												queryKey: ["config-options", "work_item_type"],
											})
										}
									/>
								))}
							</div>
						);
					})}
				</div>
			)}

			{createOpen && (
				<CreateTypeDialog
					nextOrder={types.length}
					open={createOpen}
					onOpenChange={setCreateOpen}
					onCreated={() =>
						qc.invalidateQueries({
							queryKey: ["config-options", "work_item_type"],
						})
					}
				/>
			)}
		</div>
	);
}

function TypeRow({
	type_opt,
	onChanged,
}: {
	type_opt: ConfigOption;
	onChanged: () => void;
}) {
	const qc = useQueryClient();
	const [expanded, setExpanded] = useState(false);
	const [editOpen, setEditOpen] = useState(false);

	const { t } = useTranslation("settings");
	const attrs = (type_opt.attrs?.toJson() ?? {}) as Record<string, unknown>;
	const statusDomain: string = (attrs.status_domain as string) ?? "";
	const kind: string = (attrs.kind as string) ?? "task";

	const KIND_LABEL_SINGULAR: Record<string, string> = {
		case: t("work_items_kind_case"),
		task: t("work_items_kind_task"),
		sub_task: t("work_items_kind_sub_task"),
	};

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteConfigOption({ id }),
		onSuccess: onChanged,
	});

	return (
		<Collapsible open={expanded} onOpenChange={setExpanded}>
			<div className="rounded-md border">
				<div className="flex items-center gap-3 px-4 py-2.5">
					<CollapsibleTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
						>
							<ChevronRight
								className={`h-4 w-4 transition-transform ${
									expanded ? "rotate-90" : ""
								}`}
							/>
						</Button>
					</CollapsibleTrigger>

					{type_opt.color && (
						<div
							className="h-3 w-3 rounded-full shrink-0"
							style={{ backgroundColor: type_opt.color }}
						/>
					)}

					<div className="flex-1 min-w-0">
						<span className="text-sm font-medium">
							{type_opt.displayName || type_opt.name}
						</span>
						{type_opt.displayName && type_opt.name !== type_opt.displayName && (
							<span className="text-xs text-muted-foreground ml-2">
								({type_opt.name})
							</span>
						)}
					</div>

					<div className="flex items-center gap-1.5 shrink-0">
						<Badge variant="outline" className="text-[10px]">
							{KIND_LABEL_SINGULAR[kind] ?? kind}
						</Badge>
						{statusDomain && (
							<Badge variant="secondary" className="text-[10px] font-mono">
								{statusDomain}
							</Badge>
						)}
						{type_opt.isInitial && (
							<Badge variant="secondary" className="text-[10px]">
								{t("work_items_default")}
							</Badge>
						)}
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							onClick={() => setEditOpen(true)}
						>
							<Edit2 className="h-3 w-3" />
						</Button>
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6 text-muted-foreground hover:text-destructive"
									disabled={deleteMut.isPending}
								>
									<Trash2 className="h-3 w-3" />
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>
										{t("work_items_delete_type_title", {
											name: type_opt.displayName || type_opt.name,
										})}
									</AlertDialogTitle>
									<AlertDialogDescription>
										{t("work_items_delete_type_desc")}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => deleteMut.mutate(type_opt.id)}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{t("common:delete")}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</div>

				<CollapsibleContent>
					{statusDomain && (
						<div className="border-t px-4 py-3 bg-muted/30">
							<p className="text-xs font-medium text-muted-foreground mb-2">
								{t("work_items_statuses_label", { domain: statusDomain })}
							</p>
							<ConfigDomainEditor domain={statusDomain} />
						</div>
					)}
				</CollapsibleContent>
			</div>

			{editOpen && (
				<EditConfigDialog
					option={type_opt}
					open={editOpen}
					onOpenChange={setEditOpen}
					onSaved={() => {
						onChanged();
						qc.invalidateQueries({
							queryKey: ["config-options", "work_item_type"],
						});
					}}
				/>
			)}
		</Collapsible>
	);
}

function CreateTypeDialog({
	nextOrder,
	open,
	onOpenChange,
	onCreated,
}: {
	nextOrder: number;
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: () => void;
}) {
	const [name, setName] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [color, setColor] = useState("#6b7280");
	const [kind, setKind] = useState("task");
	const { t } = useTranslation("settings");
	const [saving, setSaving] = useState(false);

	const statusDomain = name.trim()
		? `status:${name.trim().toLowerCase().replace(/\s+/g, "_")}`
		: "";

	const handleCreate = async () => {
		if (!name.trim()) return;
		setSaving(true);
		try {
			await client.createConfigOption({
				domain: "work_item_type",
				name: name.trim(),
				displayName: displayName.trim() || name.trim(),
				color,
				sortOrder: nextOrder + 1,
				attrs: Struct.fromJson({
					status_domain: statusDomain,
					kind,
				}),
			});
			onOpenChange(false);
			onCreated();
		} catch {
			/* handled */
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("work_items_new_type_title")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>
							{t("work_items_system_name")}
						</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("work_items_system_name_placeholder")}
							className="font-mono"
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_display_name")}
						</Label>
						<Input
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							placeholder={t("work_items_display_name_placeholder")}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_category")}
						</Label>
						<Select value={kind} onValueChange={setKind}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="case">
									{t("work_items_kind_case")}
								</SelectItem>
								<SelectItem value="task">
									{t("work_items_kind_task")}
								</SelectItem>
								<SelectItem value="sub_task">
									{t("work_items_kind_sub_task")}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_color")}
						</Label>
						<ColorPicker value={color} onChange={setColor} />
					</div>
					{statusDomain && (
						<div className="rounded bg-muted px-3 py-2">
							<span className="text-xs text-muted-foreground">
								{t("work_items_status_domain")}
							</span>
							<span className="text-xs font-mono">{statusDomain}</span>
						</div>
					)}
				</div>
				<DialogFooter>
					<Button onClick={handleCreate} disabled={!name.trim() || saving}>
						{saving ? t("work_items_creating") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ─── Generic config domain editor (reused for statuses, priorities, etc.) ───

function ConfigDomainEditor({ domain }: { domain: string }) {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [editOpen, setEditOpen] = useState(false);
	const [editOption, setEditOption] = useState<ConfigOption | null>(null);
	const [createOpen, setCreateOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["config-options", domain],
		queryFn: () => client.listConfigOptions({ domain }),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteConfigOption({ id }),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: ["config-options", domain] }),
	});

	const options = data?.options ?? [];

	if (isLoading) {
		return (
			<div className="flex justify-center py-8">
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="space-y-3 pt-2">
			<div className="flex justify-end">
				<Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
					<Plus className="h-3.5 w-3.5 mr-1" />
					{t("common:add")}
				</Button>
			</div>

			{options.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("work_items_no_values")}
				</p>
			) : (
				<div className="space-y-1.5">
					{options.map((opt) => (
						<div
							key={opt.id}
							className="flex items-center gap-3 rounded-md border px-4 py-2.5"
						>
							{opt.color && (
								<div
									className="h-3 w-3 rounded-full shrink-0"
									style={{ backgroundColor: opt.color }}
								/>
							)}
							<div className="flex-1 min-w-0">
								<span className="text-sm font-medium">
									{opt.displayName || opt.name}
								</span>
								{opt.displayName && opt.name !== opt.displayName && (
									<span className="text-xs text-muted-foreground ml-2">
										({opt.name})
									</span>
								)}
							</div>
							<div className="flex items-center gap-1.5 shrink-0">
								{opt.isInitial && (
									<Badge variant="secondary" className="text-[10px]">
										{t("work_items_default")}
									</Badge>
								)}
								{opt.slaSeconds > 0 && (
									<Badge variant="outline" className="text-[10px]">
										SLA {Math.floor(opt.slaSeconds / 3600)}h
									</Badge>
								)}
								<span className="text-[10px] text-muted-foreground tabular-nums w-4 text-right">
									{opt.sortOrder}
								</span>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6"
									onClick={() => {
										setEditOption(opt);
										setEditOpen(true);
									}}
								>
									<Edit2 className="h-3 w-3" />
								</Button>
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 text-muted-foreground hover:text-destructive"
											disabled={deleteMut.isPending}
										>
											<Trash2 className="h-3 w-3" />
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												{t("work_items_delete_value_title", {
													name: opt.displayName || opt.name,
												})}
											</AlertDialogTitle>
											<AlertDialogDescription>
												{t("work_items_delete_value_desc")}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>
												{t("common:cancel")}
											</AlertDialogCancel>
											<AlertDialogAction
												onClick={() => deleteMut.mutate(opt.id)}
												className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
											>
												{t("common:delete")}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>
					))}
				</div>
			)}

			{createOpen && (
				<CreateConfigDialog
					domain={domain}
					nextOrder={options.length}
					open={createOpen}
					onOpenChange={setCreateOpen}
					onCreated={() =>
						qc.invalidateQueries({ queryKey: ["config-options", domain] })
					}
				/>
			)}

			{editOpen && editOption && (
				<EditConfigDialog
					option={editOption}
					open={editOpen}
					onOpenChange={setEditOpen}
					onSaved={() =>
						qc.invalidateQueries({ queryKey: ["config-options", domain] })
					}
				/>
			)}
		</div>
	);
}

// ─── Shared components ──────────────────────────────────────────────

const DEFAULT_COLORS = [
	"#6b7280",
	"#ef4444",
	"#f97316",
	"#eab308",
	"#22c55e",
	"#3b82f6",
	"#8b5cf6",
	"#ec4899",
];

function ColorPicker({
	value,
	onChange,
}: {
	value: string;
	onChange: (v: string) => void;
}) {
	return (
		<div className="flex items-center gap-1.5">
			{DEFAULT_COLORS.map((c) => (
				<Button
					key={c}
					variant="ghost"
					size="icon"
					className="h-6 w-6 rounded-full border-2 p-0 hover:scale-110"
					style={{
						backgroundColor: c,
						borderColor: value === c ? "currentColor" : "transparent",
					}}
					onClick={() => onChange(c)}
				/>
			))}
			<Input
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="h-7 w-20 text-xs font-mono ml-1"
				placeholder="#hex"
			/>
		</div>
	);
}

function CreateConfigDialog({
	domain,
	nextOrder,
	open,
	onOpenChange,
	onCreated,
}: {
	domain: string;
	nextOrder: number;
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: () => void;
}) {
	const [name, setName] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [color, setColor] = useState("#6b7280");
	const { t } = useTranslation("settings");
	const [saving, setSaving] = useState(false);

	const handleCreate = async () => {
		if (!name.trim()) return;
		setSaving(true);
		try {
			await client.createConfigOption({
				domain,
				name: name.trim(),
				displayName: displayName.trim() || name.trim(),
				color,
				sortOrder: nextOrder + 1,
			});
			onOpenChange(false);
			onCreated();
		} catch {
			/* handled */
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("work_items_new_value_title")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>
							{t("work_items_system_name")}
						</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("work_items_name_placeholder")}
							className="font-mono"
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_display_name")}
						</Label>
						<Input
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							placeholder={t("work_items_display_placeholder")}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_color")}
						</Label>
						<ColorPicker value={color} onChange={setColor} />
					</div>
				</div>
				<DialogFooter>
					<Button onClick={handleCreate} disabled={!name.trim() || saving}>
						{saving ? t("work_items_creating") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function EditConfigDialog({
	option,
	open,
	onOpenChange,
	onSaved,
}: {
	option: ConfigOption;
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onSaved: () => void;
}) {
	const [displayName, setDisplayName] = useState(
		option.displayName || option.name,
	);
	const [color, setColor] = useState(option.color || "#6b7280");
	const [sortOrder, setSortOrder] = useState(option.sortOrder);
	const [slaSeconds, setSlaSeconds] = useState(option.slaSeconds);
	const { t } = useTranslation("settings");
	const [saving, setSaving] = useState(false);

	const handleSave = async () => {
		setSaving(true);
		try {
			await client.updateConfigOption({
				id: option.id,
				displayName,
				color,
				sortOrder,
				slaSeconds,
			});
			onOpenChange(false);
			onSaved();
		} catch {
			/* handled */
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{t("work_items_edit_title", { name: option.name })}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>
							{t("work_items_display_name")}
						</Label>
						<Input
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("work_items_color")}
						</Label>
						<ColorPicker value={color} onChange={setColor} />
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<Label>
								{t("work_items_order")}
							</Label>
							<Input
								type="number"
								value={sortOrder}
								onChange={(e) =>
									setSortOrder(Number.parseInt(e.target.value) || 0)
								}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>
								{t("work_items_sla_seconds")}
							</Label>
							<Input
								type="number"
								value={slaSeconds}
								onChange={(e) =>
									setSlaSeconds(Number.parseInt(e.target.value) || 0)
								}
								placeholder="0"
							/>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button onClick={handleSave} disabled={saving}>
						{saving ? t("work_items_saving") : t("common:save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
